import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { menuData } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';

const MenuSection = () => {
  const { language } = useLanguage();

  const translations = {
    en: {
      title: 'Our Menu',
      subtitle: 'Authentic East Coast flavors made fresh daily',
      sandwiches: 'Sandwiches',
      sides: 'Sides',
      combo: 'Combo Deals',
      extras: 'Extras',
      prepared: 'Prepared Sandwiches',
      desserts: 'Desserts'
    },
    fr: {
      title: 'Notre Menu',
      subtitle: 'Saveurs authentiques de la côte Est préparées fraîchement chaque jour',
      sandwiches: 'Les Sandwiches',
      sides: 'Accompagnements',
      combo: 'Formules',
      extras: 'Supplémentaires',
      prepared: 'Sandwichs Préparés',
      desserts: 'Desserts'
    }
  };

  const t = translations[language];

  const MenuItem = ({ item }) => (
    <Card className="bg-white/5 border-[#c9a961]/20 hover:border-[#c9a961]/60 transition-all transform hover:scale-[1.02] overflow-hidden">
      <div className="flex flex-col sm:flex-row">
        {/* Content Section */}
        <div className={`flex-1 ${item.image ? 'sm:pr-0' : ''}`}>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start gap-2">
              <CardTitle className="text-[#c9a961] text-lg sm:text-xl">
                {language === 'en' ? item.name : item.nameFr}
              </CardTitle>
              <Badge className="bg-[#c9a961] text-[#0a1929] hover:bg-[#d4b975] shrink-0">
                €{item.price}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <CardDescription className="text-white/70 leading-relaxed text-sm">
              {language === 'en' ? item.description : item.descriptionFr}
            </CardDescription>
          </CardContent>
        </div>
        
        {/* Image Section - UberEats Style */}
        {item.image && (
          <div className="sm:w-32 sm:h-32 h-40 sm:m-3 shrink-0 order-first sm:order-last">
            <img 
              src={item.image} 
              alt={language === 'en' ? item.name : item.nameFr}
              className="w-full h-full object-cover sm:rounded-lg"
            />
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <section id="menu" className="py-24 bg-[#0a1929]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#c9a961] mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Menu Tabs */}
        <Tabs defaultValue="sandwiches" className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 gap-2 bg-white/5 p-2 rounded-lg mb-12">
            <TabsTrigger value="sandwiches" className="data-[state=active]:bg-[#c9a961] data-[state=active]:text-[#0a1929] text-white/70">
              {t.sandwiches}
            </TabsTrigger>
            <TabsTrigger value="prepared" className="data-[state=active]:bg-[#c9a961] data-[state=active]:text-[#0a1929] text-white/70">
              {t.prepared}
            </TabsTrigger>
            <TabsTrigger value="sides" className="data-[state=active]:bg-[#c9a961] data-[state=active]:text-[#0a1929] text-white/70">
              {t.sides}
            </TabsTrigger>
            <TabsTrigger value="desserts" className="data-[state=active]:bg-[#c9a961] data-[state=active]:text-[#0a1929] text-white/70">
              {t.desserts}
            </TabsTrigger>
            <TabsTrigger value="combo" className="data-[state=active]:bg-[#c9a961] data-[state=active]:text-[#0a1929] text-white/70">
              {t.combo}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sandwiches" className="mt-8">
            <div className="grid gap-4 md:grid-cols-2">
              {menuData.sandwiches.map(item => (
                <MenuItem key={item.id} item={item} />
              ))}
            </div>
            
            {/* Sandwich Extras */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold text-[#c9a961] mb-4">
                {t.extras}
              </h3>
              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
                {menuData.sandwichExtras.map(item => (
                  <Card key={item.id} className="bg-white/5 border-[#c9a961]/20">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[#c9a961] font-medium">
                          {language === 'en' ? item.name : item.nameFr}
                        </span>
                        <Badge className="bg-[#c9a961] text-[#0a1929] hover:bg-[#d4b975]">
                          €{item.price}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="prepared" className="mt-8">
            <Card className="bg-[#c9a961]/10 border-[#c9a961] max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="text-[#c9a961] text-3xl text-center">
                  {language === 'en' ? menuData.preparedSandwiches.title : menuData.preparedSandwiches.titleFr}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-white/90 text-lg leading-relaxed">
                  {language === 'en' ? menuData.preparedSandwiches.description : menuData.preparedSandwiches.descriptionFr}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sides" className="mt-8">
            <div className="grid gap-4 md:grid-cols-2">
              {menuData.sides.map(item => (
                <MenuItem key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="desserts" className="mt-8">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {menuData.desserts.map(item => (
                <MenuItem key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="combo" className="mt-8">
            <Card className="bg-[#c9a961]/10 border-[#c9a961] max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="text-[#c9a961] text-2xl text-center">
                  {language === 'en' ? 'Combo Deals' : 'Formules'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-white/90 text-lg text-center">
                  <div className="mb-4">
                    <p className="font-semibold mb-2">+ €5.00</p>
                    <p className="text-white/70">
                      {language === 'en' 
                        ? 'Drink + Fries' 
                        : 'Boisson + Frites'}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">+ €6.00</p>
                    <p className="text-white/70">
                      {language === 'en' 
                        ? 'Drink + Fries + Dessert' 
                        : 'Boisson + Frites + Dessert'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default MenuSection;
